import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/PageUI";
import { useAuth } from "@/context/AuthContext";
import { UpgradeGate } from "@/components/UpgradeGate";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, X, BarChart3, LifeBuoy } from "lucide-react";

export default function Community() {
  const [polls, setPolls] = useState([]);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [audience, setAudience] = useState("both");
  const [support, setSupport] = useState({ email: "" });
  const [fb, setFb] = useState({ type: "general", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const { isPremium } = useAuth();

  const loadPolls = () => api.get("/polls").then((r) => setPolls(r.data)).catch(() => {});
  useEffect(() => {
    loadPolls();
    api.get("/support/info").then((r) => setSupport({ email: r.data.support_email })).catch(() => {});
  }, []);

  const createPoll = async () => {
    const options = opts.map((o) => o.trim()).filter(Boolean);
    if (!q.trim() || options.length < 2) return toast.error("Add a question and at least 2 options");
    setBusy(true);
    try {
      await api.post("/polls", { question: q, options, audience });
      setQ(""); setOpts(["", ""]); setAudience("both"); loadPolls(); toast.success("Poll created");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const closePoll = async (id) => { await api.put(`/polls/${id}/close`); loadPolls(); };
  const delPoll = async (id) => { await api.delete(`/polls/${id}`); loadPolls(); };
  const submitFeedback = async () => {
    if (!fb.subject.trim() || !fb.message.trim()) return toast.error("Subject and message are required");
    setBusy(true);
    try {
      await api.post("/feedback", fb);
      setFb({ type: "general", subject: "", message: "" });
      toast.success("Thanks! Your message was sent to our team.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="community-page">
      <PageHeader title="Community" subtitle="Run polls with your club and reach our support team." testid="community-header" />
      <Tabs defaultValue="polls">
        <TabsList data-testid="community-tabs">
          <TabsTrigger value="polls"><BarChart3 size={15} className="mr-1.5" /> Polls</TabsTrigger>
          <TabsTrigger value="support"><LifeBuoy size={15} className="mr-1.5" /> Support & Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="polls" className="mt-5 space-y-5">
          {!isPremium ? (
            <UpgradeGate feature="Polls & voting" desc="Create polls and gather votes from parents and players. Upgrade to Pro or Club to run polls with your club." testid="polls-upgrade-gate" />
          ) : (
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3" data-testid="create-poll-card">
            <h3 className="font-heading font-semibold text-lg">Create a poll</h3>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Poll question (e.g. What time works best for Saturday practice?)" data-testid="poll-question-input" />
            <div className="space-y-2">
              {opts.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={o} onChange={(e) => setOpts(opts.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`} />
                  {opts.length > 2 && <Button variant="ghost" size="icon" onClick={() => setOpts(opts.filter((_, j) => j !== i))}><X size={16} /></Button>}
                </div>
              ))}
              {opts.length < 10 && <Button variant="outline" size="sm" onClick={() => setOpts([...opts, ""])} data-testid="add-option-btn"><Plus size={14} className="mr-1" /> Add option</Button>}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="h-10 rounded-lg bg-background border border-border px-3 text-sm" data-testid="poll-audience-select">
                <option value="both">Parents &amp; Players</option>
                <option value="parents">Parents only</option>
                <option value="players">Players only</option>
              </select>
              <Button onClick={createPoll} disabled={busy} className="brand-gradient text-white border-0" data-testid="create-poll-btn">Create poll</Button>
            </div>
          </div>
          )}
          <div className="space-y-3">
            {polls.length === 0 && <p className="text-sm text-muted-foreground">No polls yet. Create one above.</p>}
            {polls.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`poll-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{p.question}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.total_votes} vote{p.total_votes === 1 ? "" : "s"} · {p.closed ? "Closed" : "Open"} · {p.audience === "both" ? "Parents & players" : p.audience}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {!p.closed && <button onClick={() => closePoll(p.id)} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`close-poll-${p.id}`}>Close</button>}
                    <button onClick={() => delPoll(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-poll-${p.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {p.options.map((o) => {
                    const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                    return (
                      <div key={o.id}>
                        <div className="flex justify-between text-sm mb-1"><span>{o.text}</span><span className="text-muted-foreground">{o.votes} · {pct}%</span></div>
                        <div className="h-2 rounded-full bg-accent overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="support" className="mt-5 space-y-5">
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="support-contact-card">
            <h3 className="font-heading font-semibold text-lg">Need help?</h3>
            <p className="text-sm text-muted-foreground mt-1">Email us anytime at <a href={`mailto:${support.email}`} className="text-primary underline" data-testid="support-email-link">{support.email || "support@spherehyx.com"}</a>. We usually reply within a day.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3" data-testid="feedback-card">
            <h3 className="font-heading font-semibold text-lg">Report a bug or suggest a feature</h3>
            <select value={fb.type} onChange={(e) => setFb({ ...fb, type: e.target.value })} className="h-10 rounded-lg bg-background border border-border px-3 text-sm w-full sm:w-64" data-testid="feedback-type-select">
              <option value="general">General feedback</option>
              <option value="bug">Report a bug</option>
              <option value="feature">Request a feature</option>
            </select>
            <Input value={fb.subject} onChange={(e) => setFb({ ...fb, subject: e.target.value })} placeholder="Subject" data-testid="feedback-subject-input" />
            <textarea value={fb.message} onChange={(e) => setFb({ ...fb, message: e.target.value })} rows={4} placeholder="Tell us what's on your mind..." className="w-full rounded-lg bg-background border border-border p-3 text-sm" data-testid="feedback-message-input" />
            <Button onClick={submitFeedback} disabled={busy} className="brand-gradient text-white border-0" data-testid="submit-feedback-btn">Send to our team</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
